// CaseList.jsx
// Displays a list of cases with filtering and selection functionality.

// Copyright HS Analysis GmbH, 2023
// Author: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import { useHistory } from "react-router-dom";

// MUI imports
import { makeStyles } from "@mui/styles";
import TextField from "@mui/material/TextField";

// HSA imports
import { FileViewerContext } from "../FileViewer";
import Backend from "../../common/utils/Backend";

const useStyles = makeStyles((theme) => ({
  item: {
    cursor: "pointer",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#0000001a",
    },
    padding: "10px",
    borderBottom: "1px solid #ccc",
  },
  selItem: {
    cursor: "pointer",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "#0000001a",
    },
    padding: "8px",
    border: `2px solid ${theme.palette.primary.main}`,
  },
  list: {
    listStyle: "none",
    paddingLeft: 0,
  },
  listContainer: {
    height: "100%",
    overflow: "auto",
  },
  searchBar: {
    margin: "10px 6px 0 0",
  },
}));

export default function CaseList() {
  const FV = React.useContext(FileViewerContext);
  const history = useHistory();
  const classes = useStyles();
  const [filterText, setFilterText] = React.useState("");
  const [listItems, setlistItems] = React.useState([]);
  // Holds the ordered list of fields to use when composing the case display name
  const [nameFields, setNameFields] = React.useState([]);

  // Helper: push caseId into the URL query string and clear fileId to avoid stale selection
  const updateUrlCaseId = React.useCallback(
    (newCaseId) => {
      try {
        const url = new URL(window.location.href);
        const nextCase = newCaseId == null ? null : String(newCaseId);
        if (nextCase == null || nextCase === "")
          url.searchParams.delete("caseId");
        else url.searchParams.set("caseId", nextCase);
        url.searchParams.delete("fileId");
        const newHref = `${url.pathname}${url.search}${url.hash}`;
        history.push(newHref);
      } catch {
        // ignore URL errors
      }
    },
    [history]
  );

  // Fetch column order once
  React.useEffect(() => {
    Backend.getCaseColumns().then((caseColumns) => {
      if (caseColumns) {
        // Build the ordered list of fields for the display name based on backend-configured columns
        // Only include known name parts to prevent accidental inclusion of all columns
        const allowed = ["case_acc", "vorname", "nachname"];
        const ordered = caseColumns
          .map((c) => c?.name)
          .filter((n) => allowed.includes(n));
        setNameFields(ordered);
      }
    });
  }, []);

  // Recompute items whenever list/sort or name fields change
  React.useEffect(() => {
    if (!("cases" in FV.caseList)) return;
    createItems();
  }, [FV.caseList, FV.caseSortBy, FV.caseSortReverse, nameFields]);

  /**
   * Generates a sorted and optionally reversed list of case items from FV.caseList.cases,
   * then updates the state with the resulting list.
   *
   * Each item contains the case's id, name (constructed from case_acc, vorname, nachname),
   * creation date, and change date.
   *
   * Sorting is performed based on the current FV.caseSortBy value:
   * - FV.SortByType.Date: sorts by creationDateTime
   * - FV.SortByType.ChangeDate: sorts by changeDateTime
   * - otherwise: sorts by name
   *
   * If FV.caseSortReverse is true, the sorted list is reversed.
   */
  const createItems = () => {
    const items = [];
    FV.caseList.cases.forEach((c) => {
      // Compose the display name based on backend-defined order; fallback keeps previous behavior
      const fallbackOrder = ["case_acc", "vorname", "nachname"];
      const fieldsToUse = nameFields.length > 0 ? nameFields : fallbackOrder;
      const parts = fieldsToUse.map((key) => c?.data?.[key]).filter((v) => !!v);
      const caseName = parts.join(" ");
      items.push({
        id: c.data.Id,
        name: caseName,
        creationDateTime: c.data.CreationDateTime,
        changeDateTime: c.data.ChangeDateTime,
        filePaths: c.filePaths || [],
      });
    });
    const sortedItems = items.sort((a, b) => {
      if (FV.caseSortBy === FV.SortByType.Date)
        return a.creationDateTime.localeCompare(b.creationDateTime);
      if (FV.caseSortBy === FV.SortByType.ChangeDate) {
        return a.changeDateTime.localeCompare(b.changeDateTime);
      }
      return a.name.localeCompare(b.name);
    });
    if (FV.caseSortReverse) sortedItems.reverse();
    // Apply the sorted list to state
    setlistItems(sortedItems);
  };

  /**
   * Filters the list of items based on the provided filter text.
   * Returns a new array containing only the items whose `name` property
   * includes the `filterText` (case-insensitive).
   *
   * @type {Array<Object>}
   * @property {string} name - The name property of each item in the list.
   */
  const filteredItems = React.useMemo(
    () =>
      listItems.filter((item) => {
        const itemName = item.name
          .toLowerCase()
          .includes(filterText.toLowerCase());
        return itemName;
      }),
    [listItems, filterText]
  );

  /**
   * Handles the event when a case item is clicked.
   * Updates the selected case, file path list, tree width, and triggers a renderer reset.
   *
   * @param {string|number} caseId - The unique identifier of the clicked case.
   */
  const handleItemClick = (caseId) => {
    if (FV.caseId === caseId) return;
    let clickedCase = FV.caseList.cases.find((obj) => {
      return obj.data.Id === caseId;
    });
    let pathList = [];
    for (let path of clickedCase.filePaths) {
      pathList.push({ path: path, type: "file" });
    }

    FV.setCaseId(caseId);
    clickedCase.data.Files = clickedCase.filePaths;
    FV.setSelCase(clickedCase.data);
    FV.setTreeWidth(870);
    FV.setPathList(pathList);
    FV.resetRenderer();
    // Update URL history with the selected caseId and clear any previous fileId
    updateUrlCaseId(caseId);
  };

  return (
    <div>
      <div className={classes.searchBar}>
        <TextField
          label="Filter by file name"
          variant="outlined"
          size="small"
          fullWidth
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
        />
      </div>
      {"cases" in FV.caseList && (
        <div className={classes.listContainer}>
          <ul className={classes.list}>
            {filteredItems.map((item) => (
              <li
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={
                  FV.caseId === item.id ? classes.selItem : classes.item
                }
              >
                {item.name} ({item.filePaths?.length})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
